import React, { useRef, useEffect, useState, useCallback } from 'react';
import { genUUID } from '../lib/uuid';

/* ─── Types ──────────────────────────────────────────────────── */
export interface LabeledRegion {
  id: string;
  label: string;
  tool: DrawTool;
  x1: number; y1: number;
  x2: number; y2: number;
  points?: Array<{ x: number; y: number }>;
  cx: number;
  cy: number;
}

export interface AnnotationResult {
  file: File;
  url: string;
  hasAnnotation: boolean;
  regions: LabeledRegion[];
  canvasDims: { w: number; h: number };
}

type DrawTool = 'rectangle' | 'square' | 'oval' | 'freehand';

type DrawableShape = {
  tool: DrawTool;
  x1: number; y1: number;
  x2: number; y2: number;
  points?: Array<{ x: number; y: number }>;
};

interface Props {
  imageFile: File;
  imageUrl: string;
  title?: string;
  initialRegions?: LabeledRegion[];
  onConfirm: (result: AnnotationResult) => void;
  onCancel: () => void;
}

/** Draw a rounded label pill at canvas coordinates (cx, cy). */
export function drawLabelOnCanvas(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  canvasW: number,
): void {
  const fontSize = Math.max(11, Math.min(15, canvasW / 50));
  ctx.save();
  ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
  const textW    = ctx.measureText(text).width;
  const padX = 10, padY = 5;
  const bgW  = textW + padX * 2;
  const bgH  = fontSize + padY * 2;
  const bx   = Math.max(2, Math.min(canvasW - bgW - 2, cx - bgW / 2));
  const by   = cy - bgH / 2;
  const r    = bgH / 2;

  ctx.fillStyle = 'rgba(30,27,75,0.82)';
  ctx.beginPath();
  ctx.moveTo(bx + r, by);
  ctx.lineTo(bx + bgW - r, by);
  ctx.arcTo(bx + bgW, by,        bx + bgW, by + r,       r);
  ctx.lineTo(bx + bgW, by + bgH - r);
  ctx.arcTo(bx + bgW, by + bgH,  bx + bgW - r, by + bgH, r);
  ctx.lineTo(bx + r,  by + bgH);
  ctx.arcTo(bx,       by + bgH,  bx, by + bgH - r,       r);
  ctx.lineTo(bx,      by + r);
  ctx.arcTo(bx,       by,        bx + r, by,              r);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle    = '#c7d2fe';
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(text, bx + padX, by + padY);
  ctx.restore();
}

const STROKE_COLOR = 'rgba(99,102,241,0.9)';
const FILL_COLOR   = 'rgba(99,102,241,0.18)';
const SEL_STROKE   = 'rgba(217,119,6,0.95)';
const SEL_FILL     = 'rgba(217,119,6,0.2)';
const MAX_DIM      = 1024;

const TOOLS: { id: DrawTool; label: string; icon: React.ReactNode }[] = [
  { id: 'rectangle', label: 'Rect',     icon: <RectIcon /> },
  { id: 'square',    label: 'Square',   icon: <SquareIcon /> },
  { id: 'oval',      label: 'Oval',     icon: <OvalIcon /> },
  { id: 'freehand',  label: 'Freehand', icon: <PenIcon /> },
];

function regionCenter(shape: DrawableShape): { cx: number; cy: number } {
  if (shape.tool === 'freehand' && shape.points?.length) {
    const n = shape.points.length;
    return {
      cx: shape.points.reduce((s, p) => s + p.x, 0) / n,
      cy: shape.points.reduce((s, p) => s + p.y, 0) / n,
    };
  }
  return { cx: (shape.x1 + shape.x2) / 2, cy: (shape.y1 + shape.y2) / 2 };
}

/* ─── Component ─────────────────────────────────────────────── */
export function ImageAnnotator({
  imageFile, imageUrl, title, initialRegions, onConfirm, onCancel,
}: Props) {
  const bgRef   = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef<HTMLCanvasElement>(null);

  const [tool, setTool] = useState<DrawTool>('rectangle');
  const [dims, setDims] = useState({ w: 300, h: 300 });

  /* Regions */
  const regionsRef            = useRef<LabeledRegion[]>([]);
  const [regions, setRegions] = useState<LabeledRegion[]>([]);
  const regionCounterRef      = useRef(0);

  /* Canvas selection highlight */
  const selectedIdRef         = useRef<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /* Inline rename in the regions list */
  const [editingId, setEditingId]       = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  /* Undo/redo */
  const historyRef = useRef<LabeledRegion[][]>([[]]);
  const historyIdx = useRef(0);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const pushHistory = useCallback((next: LabeledRegion[]) => {
    historyRef.current = historyRef.current.slice(0, historyIdx.current + 1);
    historyRef.current.push([...next]);
    historyIdx.current = historyRef.current.length - 1;
    setCanUndo(historyIdx.current > 0);
    setCanRedo(false);
  }, []);

  /* Drawing */
  const isDrawing    = useRef(false);
  const startPt      = useRef<{ x: number; y: number } | null>(null);
  const freePoints   = useRef<Array<{ x: number; y: number }>>([]);
  const lastMovePos  = useRef<{ clientX: number; clientY: number } | null>(null);

  /* ── Canvas helpers ──────────────────────────────────────── */
  const toCanvasPoint = useCallback((clientX: number, clientY: number) => {
    const c = drawRef.current;
    if (!c) return null;
    const r = c.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(c.width,  (clientX - r.left) * (c.width  / r.width))),
      y: Math.max(0, Math.min(c.height, (clientY - r.top)  * (c.height / r.height))),
    };
  }, []);

  const drawShape = useCallback((
    ctx: CanvasRenderingContext2D,
    s: DrawableShape,
    preview = false,
    isSelected = false,
  ) => {
    const minX = Math.min(s.x1, s.x2);
    const minY = Math.min(s.y1, s.y2);
    const w    = Math.abs(s.x2 - s.x1);
    const h    = Math.abs(s.y2 - s.y1);

    ctx.save();
    ctx.strokeStyle = isSelected ? SEL_STROKE : STROKE_COLOR;
    ctx.fillStyle   = isSelected ? SEL_FILL   : FILL_COLOR;
    ctx.lineWidth   = preview ? 2 : (isSelected ? 4 : 3);
    if (preview) ctx.setLineDash([6, 4]);

    if (s.tool === 'freehand' && s.points?.length) {
      ctx.beginPath();
      ctx.moveTo(s.points[0].x, s.points[0].y);
      for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x, s.points[i].y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    } else if (s.tool === 'oval') {
      ctx.beginPath();
      ctx.ellipse(minX + w / 2, minY + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.rect(minX, minY, w, h);
      ctx.fill();
      ctx.stroke();
      if (!preview) {
        ctx.setLineDash([]);
        ctx.fillStyle   = isSelected ? '#FBBF24' : '#fff';
        ctx.strokeStyle = isSelected ? SEL_STROKE : STROKE_COLOR;
        ctx.lineWidth   = 2;
        for (const [hx, hy] of [
          [minX, minY], [minX + w, minY], [minX, minY + h], [minX + w, minY + h],
        ] as [number, number][]) {
          ctx.beginPath();
          ctx.arc(hx, hy, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }
    }
    ctx.restore();
  }, []);

  const redrawAll = useCallback((preview?: DrawableShape | null) => {
    const c = drawRef.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, c.width, c.height);
    for (const r of regionsRef.current) {
      drawShape(ctx, r, false, r.id === selectedIdRef.current);
    }
    if (preview) drawShape(ctx, preview, true, false);
  }, [drawShape]);

  /* ── Load image & restore initial regions ────────────────── */
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      if (w > MAX_DIM || h > MAX_DIM) {
        const s = MAX_DIM / Math.max(w, h);
        w = Math.round(w * s);
        h = Math.round(h * s);
      }
      setDims({ w, h });

      if (bgRef.current) {
        bgRef.current.width  = w;
        bgRef.current.height = h;
        bgRef.current.getContext('2d')!.drawImage(img, 0, 0, w, h);
      }
      const dc = drawRef.current;
      if (dc) {
        dc.width  = w;
        dc.height = h;
        if (initialRegions?.length) {
          regionsRef.current = [...initialRegions];
          setRegions([...initialRegions]);
          regionCounterRef.current = initialRegions.length;
          historyRef.current = [[], [...initialRegions]];
          historyIdx.current = 1;
          setCanUndo(true);
          setCanRedo(false);
          const ctx = dc.getContext('2d')!;
          for (const r of initialRegions) drawShape(ctx, r, false, false);
        }
      }
    };
    img.src = imageUrl;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, drawShape]);

  /* ── Region helpers ──────────────────────────────────────── */
  const selectRegion = useCallback((id: string | null) => {
    selectedIdRef.current = id;
    setSelectedId(id);
    redrawAll();
  }, [redrawAll]);

  const deleteRegion = useCallback((id: string) => {
    const next = regionsRef.current.filter(r => r.id !== id);
    regionsRef.current = next;
    setRegions([...next]);
    pushHistory(next);
    if (selectedIdRef.current === id) {
      selectedIdRef.current = null;
      setSelectedId(null);
    }
    if (editingId === id) setEditingId(null);
    redrawAll();
  }, [redrawAll, pushHistory, editingId]);

  const openRename = useCallback((id: string) => {
    const r = regionsRef.current.find(r => r.id === id);
    if (!r) return;
    setEditingId(id);
    setEditingValue(r.label);
    selectRegion(id);
    requestAnimationFrame(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    });
  }, [selectRegion]);

  const commitRename = useCallback(() => {
    if (!editingId) return;
    const trimmed = editingValue.trim();
    if (trimmed) {
      const next = regionsRef.current.map(r =>
        r.id === editingId ? { ...r, label: trimmed } : r
      );
      regionsRef.current = next;
      setRegions([...next]);
    }
    setEditingId(null);
  }, [editingId, editingValue]);

  /* ── Pointer events ──────────────────────────────────────── */
  const onDown = useCallback((clientX: number, clientY: number) => {
    const pt = toCanvasPoint(clientX, clientY);
    if (!pt) return;
    if (editingId) commitRename();
    selectRegion(null);
    isDrawing.current = true;
    startPt.current   = pt;
    if (tool === 'freehand') {
      freePoints.current = [pt];
      const ctx = drawRef.current?.getContext('2d');
      if (ctx) {
        ctx.save();
        ctx.fillStyle = STROKE_COLOR;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }
  }, [tool, toCanvasPoint, selectRegion, editingId, commitRename]);

  const onMove = useCallback((clientX: number, clientY: number) => {
    if (!isDrawing.current || !startPt.current) return;
    const pt = toCanvasPoint(clientX, clientY);
    if (!pt) return;

    if (tool === 'freehand') {
      const prev = freePoints.current[freePoints.current.length - 1];
      freePoints.current.push(pt);
      const ctx = drawRef.current?.getContext('2d');
      if (ctx && prev) {
        ctx.save();
        ctx.strokeStyle = STROKE_COLOR;
        ctx.lineWidth   = 3;
        ctx.lineCap     = 'round';
        ctx.lineJoin    = 'round';
        ctx.beginPath();
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
        ctx.restore();
      }
    } else {
      let x2 = pt.x, y2 = pt.y;
      if (tool === 'square') {
        const dx = pt.x - startPt.current.x;
        const dy = pt.y - startPt.current.y;
        const sz = Math.max(Math.abs(dx), Math.abs(dy));
        x2 = startPt.current.x + (dx >= 0 ? sz : -sz);
        y2 = startPt.current.y + (dy >= 0 ? sz : -sz);
      }
      redrawAll({ tool, x1: startPt.current.x, y1: startPt.current.y, x2, y2 });
    }
  }, [tool, toCanvasPoint, redrawAll]);

  const onUp = useCallback((clientX: number, clientY: number) => {
    if (!isDrawing.current || !startPt.current) return;
    isDrawing.current = false;

    const pt = toCanvasPoint(clientX, clientY);
    if (!pt) { startPt.current = null; return; }

    let newRegion: LabeledRegion | null = null;

    if (tool === 'freehand') {
      if (freePoints.current.length > 2) {
        const shape: DrawableShape = {
          tool: 'freehand', x1: 0, y1: 0, x2: 0, y2: 0,
          points: [...freePoints.current],
        };
        regionCounterRef.current += 1;
        const { cx, cy } = regionCenter(shape);
        newRegion = { ...shape, id: genUUID(), label: `Region ${regionCounterRef.current}`, cx, cy };
        regionsRef.current = [...regionsRef.current, newRegion];
        setRegions([...regionsRef.current]);
        pushHistory(regionsRef.current);
        redrawAll(null);
      }
      freePoints.current = [];
    } else {
      let x2 = pt.x, y2 = pt.y;
      if (tool === 'square') {
        const dx = pt.x - startPt.current.x;
        const dy = pt.y - startPt.current.y;
        const sz = Math.max(Math.abs(dx), Math.abs(dy));
        x2 = startPt.current.x + (dx >= 0 ? sz : -sz);
        y2 = startPt.current.y + (dy >= 0 ? sz : -sz);
      }
      const rw = Math.abs(x2 - startPt.current.x);
      const rh = Math.abs(y2 - startPt.current.y);
      if (rw > 8 && rh > 8) {
        const shape: DrawableShape = { tool, x1: startPt.current.x, y1: startPt.current.y, x2, y2 };
        regionCounterRef.current += 1;
        const { cx, cy } = regionCenter(shape);
        newRegion = { ...shape, id: genUUID(), label: `Region ${regionCounterRef.current}`, cx, cy };
        regionsRef.current = [...regionsRef.current, newRegion];
        setRegions([...regionsRef.current]);
        pushHistory(regionsRef.current);
        redrawAll(null);
      }
    }

    startPt.current = null;
    if (newRegion) {
      selectRegion(newRegion.id);
      // Auto-open rename for the newly drawn region
      const id = newRegion.id;
      const label = newRegion.label;
      setEditingId(id);
      setEditingValue(label);
      requestAnimationFrame(() => {
        editInputRef.current?.focus();
        editInputRef.current?.select();
      });
    }
  }, [tool, toCanvasPoint, redrawAll, selectRegion, pushHistory]);

  const onPtrDown = (e: React.PointerEvent) => {
    e.preventDefault();
    drawRef.current?.setPointerCapture(e.pointerId);
    onDown(e.clientX, e.clientY);
  };
  const onPtrMove = (e: React.PointerEvent) => {
    lastMovePos.current = { clientX: e.clientX, clientY: e.clientY };
    onMove(e.clientX, e.clientY);
  };
  const onPtrUp = (e: React.PointerEvent) => onUp(e.clientX, e.clientY);
  const onPtrCancel = (_e: React.PointerEvent) => {
    if (!isDrawing.current) return;
    if (lastMovePos.current) {
      onUp(lastMovePos.current.clientX, lastMovePos.current.clientY);
    } else {
      isDrawing.current = false;
      startPt.current = null;
      freePoints.current = [];
      const c = drawRef.current;
      if (c) {
        const ctx = c.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, c.width, c.height);
        redrawAll();
      }
    }
    lastMovePos.current = null;
  };

  /* ── Undo / Redo ─────────────────────────────────────────── */
  const handleUndo = useCallback(() => {
    if (historyIdx.current <= 0) return;
    if (editingId) { setEditingId(null); }
    historyIdx.current -= 1;
    const prev = historyRef.current[historyIdx.current];
    regionsRef.current = [...prev];
    setRegions([...prev]);
    selectedIdRef.current = null;
    setSelectedId(null);
    setCanUndo(historyIdx.current > 0);
    setCanRedo(true);
    redrawAll();
  }, [redrawAll, editingId]);

  const handleRedo = useCallback(() => {
    if (historyIdx.current >= historyRef.current.length - 1) return;
    if (editingId) { setEditingId(null); }
    historyIdx.current += 1;
    const next = historyRef.current[historyIdx.current];
    regionsRef.current = [...next];
    setRegions([...next]);
    selectedIdRef.current = null;
    setSelectedId(null);
    setCanUndo(true);
    setCanRedo(historyIdx.current < historyRef.current.length - 1);
    redrawAll();
  }, [redrawAll, editingId]);

  /* ── Clear / Confirm ─────────────────────────────────────── */
  const handleClear = () => {
    regionsRef.current = [];
    setRegions([]);
    pushHistory([]);
    regionCounterRef.current = 0;
    selectedIdRef.current = null;
    setSelectedId(null);
    setEditingId(null);
    freePoints.current = [];
    const ctx = drawRef.current?.getContext('2d');
    if (ctx && drawRef.current) ctx.clearRect(0, 0, drawRef.current.width, drawRef.current.height);
  };

  const handleSkip = () =>
    onConfirm({ file: imageFile, url: imageUrl, hasAnnotation: false, regions: [], canvasDims: { w: dims.w, h: dims.h } });

  const handleConfirm = () => {
    if (editingId) commitRename();
    const finalRegions = regionsRef.current;
    const bg   = bgRef.current;
    const draw = drawRef.current;
    if (!bg) { handleSkip(); return; }

    const canvasDims = { w: bg.width, h: bg.height };

    const out = document.createElement('canvas');
    out.width  = bg.width;
    out.height = bg.height;
    const ctx  = out.getContext('2d')!;
    ctx.drawImage(bg, 0, 0);
    if (draw && finalRegions.length > 0) {
      ctx.drawImage(draw, 0, 0);
      // Burn region labels onto the output canvas at each region's center
      for (const r of finalRegions) {
        drawLabelOnCanvas(ctx, r.label, r.cx, r.cy, bg.width);
      }
    }

    try {
      out.toBlob(blob => {
        if (!blob) { handleSkip(); return; }
        const file = new File([blob], imageFile.name, { type: 'image/jpeg' });
        const url  = URL.createObjectURL(blob);
        onConfirm({ file, url, hasAnnotation: finalRegions.length > 0, regions: finalRegions, canvasDims });
      }, 'image/jpeg', 0.88);
    } catch {
      handleSkip();
    }
  };

  const hasRegions = regions.length > 0;

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div className="annotator-overlay">
      <div className="annotator-sheet">

        {/* Header */}
        <div className="annotator-header">
          <button className="annotator-x" onClick={onCancel} aria-label="Cancel">✕</button>
          <span className="annotator-title">{title || 'Mark region to update'}</span>
          <div style={{ width: 36 }} />
        </div>

        {/* Canvas */}
        <div className="annotator-canvas-area">
          <div className="annotator-canvas-wrap" style={{ aspectRatio: `${dims.w} / ${dims.h}` }}>
            <canvas ref={bgRef} className="annotator-bg" />
            <canvas
              ref={drawRef}
              className="annotator-draw annotator-draw-shapes"
              onPointerDown={onPtrDown}
              onPointerMove={onPtrMove}
              onPointerUp={onPtrUp}
              onPointerCancel={onPtrCancel}
            />
            {/* Canvas label bubbles */}
            <div className="annotator-label-overlay">
              {regions.map(region => {
                const lx = Math.max(5, Math.min(95, (region.cx / dims.w) * 100));
                const ly = Math.max(5, Math.min(95, (region.cy / dims.h) * 100));
                return (
                  <div
                    key={region.id}
                    className={`annotator-label-bubble${region.id === selectedId ? ' annotator-label-bubble--selected' : ''}`}
                    style={{ left: `${lx}%`, top: `${ly}%` }}
                    onPointerDown={e => { e.stopPropagation(); openRename(region.id); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && openRename(region.id)}
                    aria-label={`Rename region: ${region.label}`}
                  >
                    {region.label}
                  </div>
                );
              })}
            </div>
          </div>

          <p className="caption" style={{ textAlign: 'center', marginTop: 6, color: 'var(--text-faint)' }}>
            {hasRegions
              ? 'Draw more regions, or rename / delete below'
              : 'Draw a region — AI will focus on the marked area'
            }
          </p>
        </div>

        {/* Toolbar */}
        <div className="annotator-toolbar">

          {/* Row 1: Drawing tools + Undo/Redo */}
          <div className="annotator-controls-row">
            <div className="annotator-shape-tools">
              {TOOLS.map(t => (
                <button
                  key={t.id}
                  className={`annotator-shape-btn${tool === t.id ? ' active' : ''}`}
                  onClick={() => setTool(t.id)}
                  aria-label={t.label}
                  title={t.label}
                  type="button"
                >
                  <span className="annotator-shape-icon">{t.icon}</span>
                  <span className="annotator-shape-label">{t.label}</span>
                </button>
              ))}
            </div>
            <div className="annotator-history-btns">
              <button
                type="button"
                className="annotator-hist-btn"
                onClick={handleUndo}
                disabled={!canUndo}
                aria-label="Undo"
                title="Undo"
              >
                <UndoIcon /><span>Undo</span>
              </button>
              <button
                type="button"
                className="annotator-hist-btn"
                onClick={handleRedo}
                disabled={!canRedo}
                aria-label="Redo"
                title="Redo"
              >
                <RedoIcon /><span>Redo</span>
              </button>
            </div>
          </div>

          {/* Row 2: Regions list */}
          {hasRegions && (
            <div className="annotator-regions-panel">
              <div className="annotator-regions-header">
                <span className="annotator-regions-count">{regions.length} region{regions.length !== 1 ? 's' : ''}</span>
                <button
                  type="button"
                  className="annotator-clear"
                  onClick={handleClear}
                >
                  Clear All
                </button>
              </div>
              <div className="annotator-regions-list">
                {regions.map(region => (
                  <div
                    key={region.id}
                    className={`annotator-region-row${region.id === selectedId ? ' annotator-region-row--selected' : ''}`}
                  >
                    {editingId === region.id ? (
                      <>
                        <input
                          ref={editInputRef}
                          className="annotator-region-input"
                          type="text"
                          value={editingValue}
                          onChange={e => setEditingValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') commitRename();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          placeholder="Region name…"
                          maxLength={40}
                          style={{ fontSize: '16px' }}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="annotator-region-save-btn"
                          onClick={commitRename}
                          aria-label="Save name"
                        >
                          <CheckIcon />
                        </button>
                      </>
                    ) : (
                      <>
                        <span
                          className="annotator-region-name"
                          onClick={() => openRename(region.id)}
                          title="Click to rename"
                        >
                          {region.label}
                        </span>
                        <button
                          type="button"
                          className="annotator-region-edit-btn"
                          onClick={() => openRename(region.id)}
                          aria-label={`Rename ${region.label}`}
                          title="Rename"
                        >
                          <PencilIcon />
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="annotator-region-delete-btn"
                      onClick={() => deleteRegion(region.id)}
                      aria-label={`Delete ${region.label}`}
                      title="Delete region"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="annotator-actions">
          <button
            className="btn btn-secondary btn-sm"
            style={{ flex: 1 }}
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            style={{ flex: 2 }}
            onClick={handleConfirm}
            type="button"
          >
            {hasRegions
              ? `Confirm (${regions.length} region${regions.length !== 1 ? 's' : ''})`
              : 'Continue without marking'
            }
          </button>
        </div>

      </div>
    </div>
  );
}

/* ─── Tool icons ─────────────────────────────────────────────── */
function RectIcon() {
  return (
    <svg width="20" height="16" viewBox="0 0 20 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <rect x="1" y="1" width="18" height="14" rx="1.5" />
    </svg>
  );
}
function SquareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <rect x="1" y="1" width="14" height="14" rx="1.5" />
    </svg>
  );
}
function OvalIcon() {
  return (
    <svg width="22" height="16" viewBox="0 0 22 16" fill="none" stroke="currentColor" strokeWidth="1.8">
      <ellipse cx="11" cy="8" rx="10" ry="7" />
    </svg>
  );
}
function PenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}
function UndoIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7v6h6" />
      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
    </svg>
  );
}
function RedoIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 7v6h-6" />
      <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
    </svg>
  );
}
